import * as React from 'react';

/**
 * Focus / selection preservation across state transitions.
 *
 * Lives beside the degraded-state surfaces because it is what makes them
 * survivable for a keyboard-only operator: without it, every transient
 * failure that swaps the content also throws focus back to `<body>`.
 */

export interface PreservedFocusOptions<TSelection> {
  /** Current selection value; restored if it still exists after a transition. */
  selection?: TSelection;
  /** Does this selection still exist in the newly-arrived data? */
  stillPresent?: (selection: TSelection) => boolean;
  /** Called when the selection no longer exists and must be dropped. */
  onSelectionLost?: (selection: TSelection) => void;
}

export interface PreservedFocusResult {
  /**
   * Attach to the container that swaps between degraded and settled
   * content. Focus inside it is captured before the swap and restored
   * after, keyed on `data-focus-key`.
   */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Manually capture the current focus key (before an imperative refresh). */
  capture: () => void;
  /** The focus key captured on the last transition, if any. */
  lastFocusKey: string | null;
}

/**
 * Keep keyboard focus and row selection alive across a
 * settled → degraded → settled transition.
 *
 * Without this, a transient 503 unmounts the table, throws focus back to
 * `<body>`, and a keyboard-only operator loses their place mid-shift — the
 * exact failure mode the shift-floor requirement calls out. Elements opt in
 * by carrying a stable `data-focus-key`.
 */
export function usePreservedFocus<TSelection>(
  /** Whatever value flips when the view swaps between states. */
  stateKey: unknown,
  options: PreservedFocusOptions<TSelection> = {},
): PreservedFocusResult {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const focusKeyRef = React.useRef<string | null>(null);
  const { selection, stillPresent, onSelectionLost } = options;

  const capture = React.useCallback(() => {
    const container = containerRef.current;
    const active = container?.ownerDocument?.activeElement;
    if (!container || !active || !(active instanceof HTMLElement)) return;
    if (!container.contains(active)) return;
    const holder = active.closest<HTMLElement>('[data-focus-key]');
    if (holder) focusKeyRef.current = holder.getAttribute('data-focus-key');
  }, []);

  // Record focus AS IT HAPPENS, not on effect cleanup. React runs passive
  // cleanup after the DOM mutation, by which point the focused row is
  // already detached and `document.activeElement` has fallen back to
  // `<body>` — so a cleanup-time read always captured nothing.
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onFocusIn = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const holder = target.closest<HTMLElement>('[data-focus-key]');
      if (holder) focusKeyRef.current = holder.getAttribute('data-focus-key');
    };
    container.addEventListener('focusin', onFocusIn);
    return () => container.removeEventListener('focusin', onFocusIn);
  }, []);

  // Restore after the transition, but only when focus was actually LOST.
  // Re-focusing while the operator is already somewhere inside the
  // container would yank the caret out from under them on every refetch.
  React.useEffect(() => {
    const container = containerRef.current;
    const key = focusKeyRef.current;
    if (!container || !key) return;
    const active = container.ownerDocument?.activeElement;
    if (active instanceof HTMLElement && container.contains(active)) return;

    const escaped =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(key)
        : key.replace(/["\\]/g, '\\$&');
    const holder = container.querySelector<HTMLElement>(`[data-focus-key="${escaped}"]`);
    if (!holder) return;
    const focusable = holder.matches(
      'a[href],button,input,select,textarea,[tabindex]:not([tabindex="-1"])',
    )
      ? holder
      : holder.querySelector<HTMLElement>(
          'a[href],button,input,select,textarea,[tabindex]:not([tabindex="-1"])',
        );
    focusable?.focus();
  }, [stateKey]);

  // Drop a selection that no longer exists in the newly-arrived data,
  // rather than leaving the view pointing at a row that is gone.
  React.useEffect(() => {
    if (selection === undefined || selection === null) return;
    if (!stillPresent) return;
    if (!stillPresent(selection)) onSelectionLost?.(selection);
  }, [stateKey, selection, stillPresent, onSelectionLost]);

  return { containerRef, capture, lastFocusKey: focusKeyRef.current };
}
